import { useMemo } from "react";
import { Chart } from "primereact/chart";
import { Message } from "primereact/message";

import { EmptyState, PageHeader } from "../../components/ui";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  PointMetrics as PointMetricsData,
  PointMetricsWeek,
  countPointRedemptions,
  formatMetricInteger,
} from "../../models/pointMetrics";
import { ReportPeriodFilter } from "../../components/Filters/ReportPeriodFilter";
import { useReportPeriod } from "../../hooks/useReportPeriod";
import { useGetPointMetricsQuery } from "../../store/services/point-metrics";
import { toReportDateParam } from "../../utils/constants/report-date-range";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import {
  dayChartInteraction,
  dayChartScales,
  dayLineDataset,
  formatDayLabel,
  tenantDayLegend,
} from "../../utils/functions/benefitsMetricsPresentation";
import {
  BenefitsMetricsLoadingOverlay,
  BenefitsMetricsRefreshingIndicator,
  BenefitsMetricsTenantScopeHeader,
} from "../Reporting/components/BenefitsMetricsWidgets";
import { PointMetricsKpiCard } from "./components/PointMetricsKpiCard";
import { PointsPageCard, PointsPageLoading } from "../PointRules/components/PointsPageCard";
import { POINT_RULE_BLOCK_TITLE_STYLE } from "../PointRules/components/PointRulePreview";

const PAGE_TITLE = "Dashboard de Puntos";
const NO_VALUE = "—";
const ERROR_FALLBACK = "No se pudieron cargar las métricas.";

// H3 de la escala (§3.3), el mismo de los bloques de Reglas. La bajada queda pegada al título
// (§5.1, 6px como label ↔ campo) y la separación de sección (16px) va antes del gráfico.
const SECTION_TITLE_STYLE = { ...POINT_RULE_BLOCK_TITLE_STYLE, margin: "0 0 var(--space-6)" };

// Mismo trazo y leyenda que "Canjes por día" en Métricas de beneficios. Eje Y y tooltip en es-AR
// como las tarjetas: Chart.js formatea con el locale del navegador (3,000 en vez de 3.000).
const POINT_METRICS_CHART_OPTIONS = {
  maintainAspectRatio: false,
  layout: { padding: { top: 8, bottom: 8 } },
  interaction: dayChartInteraction,
  plugins: {
    legend: tenantDayLegend,
    tooltip: {
      enabled: true,
      mode: "point" as const,
      intersect: true,
      callbacks: {
        title: (items: { label: string }[]) => (items[0] ? `Semana del ${items[0].label}` : ""),
        label: (context: { dataset: { label?: string }; parsed: { y: number | null } }) =>
          `${context.dataset.label ?? ""}: ${formatMetricInteger(context.parsed.y ?? 0)}`,
      },
    },
  },
  scales: {
    ...dayChartScales,
    y: {
      ...dayChartScales.y,
      ticks: {
        ...dayChartScales.y.ticks,
        callback: (value: number | string) => formatMetricInteger(Number(value)),
      },
    },
  },
};

// El canvas no resuelve var(--...): el color se lee del :root al armar el dataset.
const readCssToken = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const buildWeeklyChartData = (weeklySeries: PointMetricsWeek[]) => ({
  labels: weeklySeries.map((week) => formatDayLabel(week.weekStart)),
  datasets: [
    dayLineDataset(
      "Puntos emitidos",
      readCssToken("--success-default"),
      weeklySeries.map((week) => week.pointsIssued)
    ),
    dayLineDataset(
      "Puntos canjeados",
      readCssToken("--info-default"),
      weeklySeries.map((week) => week.pointsRedeemed)
    ),
  ],
});

// Las bajadas siguen lo que calcula GET /bo/points/metrics. Usuarios con puntos es una foto de
// hoy (el backend no la filtra por rango): por eso va última y lo aclara. Las cuatro van en una
// fila, como el resumen del reporte de push.
const buildPeriodKpiCards = (metrics: PointMetricsData | undefined) => [
  {
    label: "Puntos emitidos",
    value: metrics ? formatMetricInteger(metrics.emission.pointsIssued) : NO_VALUE,
    description: "Puntos otorgados en el período. No incluye devoluciones de canjes.",
  },
  {
    label: "Canjes",
    value: metrics ? formatMetricInteger(countPointRedemptions(metrics)) : NO_VALUE,
    description: "Canjes hechos en el período, en cualquier estado.",
  },
  {
    label: "Pendientes de validación",
    value: metrics ? formatMetricInteger(metrics.redemptions.pendingValidation) : NO_VALUE,
    description: "Canjes del período con el QR vigente que todavía no se validaron.",
  },
];

// La racha promedio no se muestra: las rachas todavía no se lanzan.
const buildCurrentKpiCards = (metrics: PointMetricsData | undefined) => [
  {
    label: "Usuarios con puntos",
    value: metrics ? formatMetricInteger(metrics.engagement.usersWithBalance) : NO_VALUE,
    description: "Usuarios con saldo mayor a 0 hoy. No depende del período.",
  },
];

export const PointMetrics = () => {
  const scope = useTenantScope();
  const pointsFlag = useIsFeatureFlagEnabled("gamification_points_enabled");
  // SUPER_ADMIN en "Todos" ve todas las instituciones: ahí no aplica el flag de un tenant.
  const isAllTenantsScope = !scope.loading && scope.role === "SUPER_ADMIN" && scope.tenantId === null;
  const flagReady = isAllTenantsScope || (!pointsFlag.loading && pointsFlag.enabled);
  // Mismo filtro de período que Reportes y Auditoría: presets + "Personalizado".
  const period = useReportPeriod();

  const {
    currentData: metrics,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetPointMetricsQuery(
    {
      tenantId: scope.tenantId,
      from: toReportDateParam(period.dateFrom),
      to: toReportDateParam(period.dateTo),
    },
    { skip: scope.loading || !flagReady || !period.rangeIsValid }
  );

  const weeklySeries = metrics?.weeklySeries;
  const weeklyChartData = useMemo(() => buildWeeklyChartData(weeklySeries ?? []), [weeklySeries]);

  if (scope.loading) {
    return <PointsPageLoading title={PAGE_TITLE} />;
  }

  if (!isAllTenantsScope) {
    if (pointsFlag.loading) {
      return <PointsPageLoading title={PAGE_TITLE} />;
    }
    if (!pointsFlag.enabled) {
      return (
        <PointsPageCard>
          <PageHeader title={PAGE_TITLE} />
          <div className="flex justify-content-center p-4">
            <Message severity="info" text="La sección está deshabilitada para este tenant." />
          </div>
        </PointsPageCard>
      );
    }
  }

  return (
    <PointsPageCard>
      <BenefitsMetricsLoadingOverlay visible={isLoading} />
      <BenefitsMetricsTenantScopeHeader tenantId={scope.tenantId} />
      <PageHeader title={PAGE_TITLE} />

      {/* Habilitado durante el refresco: cada rango es su propia entrada de cache (AGENTS.md). */}
      <ReportPeriodFilter period={period} idPrefix="points-metrics" disabled={isLoading} />
      <div className="flex justify-content-end mb-2">
        <BenefitsMetricsRefreshingIndicator visible={isFetching && !isLoading} />
      </div>

      {isError ? (
        <Message severity="error" text={getApiErrorMessage(error) ?? ERROR_FALLBACK} />
      ) : (
        <>
          <div className="grid">
            {buildPeriodKpiCards(metrics).map((card) => (
              <PointMetricsKpiCard key={card.label} {...card} columnClassName="col-12 md:col-3" />
            ))}
            {buildCurrentKpiCards(metrics).map((card) => (
              <PointMetricsKpiCard key={card.label} {...card} columnClassName="col-12 md:col-3" />
            ))}
          </div>

          <section style={{ marginTop: "var(--space-32)" }}>
            <h3 style={SECTION_TITLE_STYLE}>Evolución semanal</h3>
            <small className="text-color-secondary block" style={{ marginBottom: "var(--space-16)" }}>
              Puntos emitidos y canjeados por semana. Cada semana arranca el lunes: la primera y la
              última pueden tener días fuera del período, que no se cuentan.
            </small>
            {metrics && metrics.weeklySeries.length === 0 ? (
              <EmptyState entity="movimientos" />
            ) : (
              <div style={{ height: "280px" }}>
                {metrics && (
                  <Chart
                    type="line"
                    data={weeklyChartData}
                    options={POINT_METRICS_CHART_OPTIONS}
                    className="h-full w-full"
                  />
                )}
              </div>
            )}
          </section>
        </>
      )}
    </PointsPageCard>
  );
};
